import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileSpreadsheet, FileText, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_CONFIGS = [
  { key: 'inventario', label: 'Inventario Actual', desc: 'Existencias por almacén, grupo y estado', icon: BarChart3, endpoint: '/reportes/inventario' },
  { key: 'entradas-repartidor', label: 'Entradas por Repartidor', desc: 'Total por cada repartidor', icon: FileText, endpoint: '/reportes/entradas-repartidor' },
  { key: 'entradas-cliente', label: 'Entradas por Cliente', desc: 'Total por cliente', icon: FileText, endpoint: '/reportes/entradas-cliente' },
  { key: 'tarimas', label: 'Tarimas', desc: 'Estado y progreso de tarimas', icon: FileText, endpoint: '/reportes/tarimas' },
  { key: 'embarques', label: 'Salidas de Camión', desc: 'Historial de embarques', icon: FileText, endpoint: '/reportes/embarques' },
  { key: 'revisiones-pendientes', label: 'Revisiones Pendientes', desc: 'Material en revisión sin devolver', icon: FileText, endpoint: '/reportes/revisiones-pendientes' },
  { key: 'garantias', label: 'Salidas por Garantía', desc: 'Acumuladores enviados a garantía', icon: FileText, endpoint: '/reportes/garantias' },
  { key: 'almacen-led', label: 'Almacén Ledezma', desc: 'Inventario especial Ledezma', icon: FileText, endpoint: '/reportes/almacen-especial/LED' },
  { key: 'almacen-mar', label: 'Almacén Mario', desc: 'Inventario especial Mario', icon: FileText, endpoint: '/reportes/almacen-especial/MAR' },
];

export default function Reportes() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => { API.get('/catalogs/warehouses').then(res => setWarehouses(res.data)); }, []);

  const loadReport = async (config) => {
    setSelectedReport(config); setLoading(true);
    try {
      const params = {};
      if (config.key === 'inventario' && warehouseId) params.warehouse_id = warehouseId;
      const res = await API.get(config.endpoint, { params }); setData(res.data);
    } catch { toast.error('Error cargando reporte'); } finally { setLoading(false); }
  };

  const downloadExcel = async (config) => {
    try {
      const params = { formato: 'excel' };
      if (config.key === 'inventario' && warehouseId) params.warehouse_id = warehouseId;
      const token = localStorage.getItem('token');
      const queryStr = new URLSearchParams(params).toString();
      const url = `${BACKEND_URL}/api${config.endpoint}?${queryStr}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = downloadUrl;
      a.download = `reporte_${config.key}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(downloadUrl);
      toast.success('Archivo descargado');
    } catch { toast.error('Error descargando archivo'); }
  };

  const renderData = () => {
    if (!data || !data.datos) return null;
    const rows = data.datos;
    if (rows.length === 0) return <p className="text-center text-muted-foreground py-8">Sin datos</p>;
    const keys = Object.keys(rows[0]);
    return (<div className="overflow-x-auto"><Table><TableHeader><TableRow className="bg-muted/30">
      {keys.map(k => <TableHead key={k} className="capitalize">{k.replace(/_/g, ' ')}</TableHead>)}
    </TableRow></TableHeader><TableBody>
      {rows.map((row, i) => (<TableRow key={i} className="hover:bg-muted/40">
        {keys.map(k => <TableCell key={k}>{typeof row[k] === 'number' ? row[k].toLocaleString() : String(row[k] || '-')}</TableCell>)}
      </TableRow>))}
    </TableBody></Table></div>);
  };

  return (
    <div className="space-y-4">
      <div><h1 className="text-xl font-semibold">Reportes</h1><p className="text-sm text-muted-foreground">Generación y descarga de reportes</p></div>
      {selectedReport?.key === 'inventario' && (<Card className="bg-white border"><CardContent className="p-4"><div className="flex gap-3 items-end">
        <div><label className="text-sm font-medium">Filtrar por almacén</label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger className="w-[220px] mt-1"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
          </Select></div>
        <Button variant="outline" onClick={() => loadReport(selectedReport)}>Actualizar</Button>
      </div></CardContent></Card>)}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {REPORT_CONFIGS.map(config => (
          <Card key={config.key} className={`bg-white border cursor-pointer hover:shadow-md transition-shadow ${selectedReport?.key === config.key ? 'ring-2 ring-[#0F6E87]' : ''}`} onClick={() => loadReport(config)}>
            <CardContent className="p-4"><div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0"><config.icon className="h-4 w-4" /></div>
              <div className="min-w-0"><p className="text-sm font-medium">{config.label}</p><p className="text-xs text-muted-foreground mt-0.5">{config.desc}</p></div>
            </div></CardContent>
          </Card>
        ))}
      </div>
      {selectedReport && (<Card className="bg-white border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div><CardTitle className="text-base">{selectedReport.label}</CardTitle>
            {data && <p className="text-xs text-muted-foreground mt-1">{data.registros ?? data.datos?.length ?? 0} registros</p>}</div>
          <Button variant="outline" size="sm" onClick={() => downloadExcel(selectedReport)}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Exportar Excel
          </Button>
        </CardHeader>
        <CardContent className="p-0">{loading ? <p className="text-center py-8 text-muted-foreground">Generando...</p> : renderData()}</CardContent>
      </Card>)}
    </div>
  );
}
